import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Users, Building, CreditCard, Calendar, Shield, Mail, Phone } from 'lucide-react';

const AllEforms = () => {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'active', 'coming-soon'

  const forms = [
    {
      id: 'e-form',
      title: 'Personal Information Form',
      description: 'Electronic form for basic employee information and registration',
      route: '/e-form',
      icon: <FileText className="w-8 h-8" />,
      category: 'Basic Forms',
      color: 'bg-blue-500',
      hoverColor: 'hover:bg-blue-600',
      status: 'Active'
    },
    {
      id: 'id-card',
      title: 'ID Card',
      description: 'ID card for employees',
      route: '/id-card',
      icon: <FileText className="w-8 h-8" />,
      category: 'Basic Forms',
      color: 'bg-blue-500',
      hoverColor: 'hover:bg-blue-600',
      status: 'Active'
    },
    {
      id: 'joining-report',
      title: 'Joining Report',
      description: 'Joining report for new employees',
      route: '/joining-report',
      icon: <FileText className="w-8 h-8" />,
      category: 'Basic Forms',
      color: 'bg-blue-500',
      hoverColor: 'hover:bg-blue-600',
      status: 'Active'
    },
    {
      id: 'form-f',
      title: 'Form F',
      description: 'Family declaration form for employee dependents and nominees',
      route: '/form-f',
      icon: <Users className="w-8 h-8" />,
      category: 'Family Forms',
      color: 'bg-green-500',
      hoverColor: 'hover:bg-green-600',
      status: 'Active'
    },
    {
      id: 'form-11',
      title: 'EPF Form 11',
      description: 'Employees Provident Fund Scheme declaration form for new employees',
      route: '/form-11',
      icon: <Building className="w-8 h-8" />,
      category: 'EPF Forms',
      color: 'bg-purple-500',
      hoverColor: 'hover:bg-purple-600',
      status: 'Active'
    },
    // {
    //   id: 'form-2',
    //   title: 'EPF Form 2',
    //   description: 'Nomination form for Provident Fund benefits',
    //   route: '/form-2',
    //   icon: <CreditCard className="w-8 h-8" />,
    //   category: 'EPF Forms',
    //   color: 'bg-orange-500',
    //   hoverColor: 'hover:bg-orange-600',
    //   status: 'Coming Soon'
    // },
    // {
    //   id: 'form-5',
    //   title: 'EPF Form 5',
    //   description: 'Application for advance from Provident Fund',
    //   route: '/form-5',
    //   icon: <Calendar className="w-8 h-8" />,
    //   category: 'EPF Forms',
    //   color: 'bg-red-500',
    //   hoverColor: 'hover:bg-red-600',
    //   status: 'Coming Soon'
    // },
    // {
    //   id: 'form-19',
    //   title: 'EPF Form 19',
    //   description: 'Application for final settlement of Provident Fund account',
    //   route: '/form-19',
    //   icon: <Shield className="w-8 h-8" />,
    //   category: 'EPF Forms',
    //   color: 'bg-indigo-500',
    //   hoverColor: 'hover:bg-indigo-600',
    //   status: 'Coming Soon'
    // }
  ];

  const handleFormNavigation = (route, status) => {
    if (status === 'Active') {
      // Open in new tab
      window.open(route, '_blank');
    }
  };

  const handleFormEdit = (route, status) => {
    if (status === 'Active') {
      // Navigate in same tab for editing
      navigate(route);
    }
  };

  // Filter forms based on active filter
  const getFilteredForms = () => {
    switch (activeFilter) {
      case 'active':
        return forms.filter(form => form.status === 'Active');
      case 'coming-soon':
        return forms.filter(form => form.status === 'Coming Soon');
      default:
        return forms;
    }
  };

  const filteredForms = getFilteredForms();
  const categories = [...new Set(filteredForms.map(form => form.category))];

  // Filter statistics
  const totalForms = forms.length;
  const activeForms = forms.filter(form => form.status === 'Active').length;
  const comingSoonForms = forms.filter(form => form.status === 'Coming Soon').length;

  const handleFilterClick = (filter) => {
    setActiveFilter(filter);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Forms
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Manage and navigate to all available forms. Click on any form to open it in a new tab or use the edit button to modify existing forms.
          </p>
        </div>

        {/* Statistics Cards with Filter Functionality */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          <div 
            className={`rounded-lg shadow-md p-6 cursor-pointer transition-all duration-200 transform hover:scale-105 ${
              activeFilter === 'all' 
                ? 'bg-blue-500 text-white ring-4 ring-blue-300' 
                : 'bg-white hover:shadow-lg'
            }`}
            onClick={() => handleFilterClick('all')}
          >
            <div className="flex items-center">
              <FileText className={`w-8 h-8 ${activeFilter === 'all' ? 'text-white' : 'text-blue-500'}`} />
              <div className="ml-4">
                <p className={`text-sm font-medium ${activeFilter === 'all' ? 'text-blue-100' : 'text-gray-600'}`}>
                  Total Forms
                </p>
                <p className={`text-2xl font-bold ${activeFilter === 'all' ? 'text-white' : 'text-gray-900'}`}>
                  {totalForms}
                </p>
              </div>
            </div>
            {activeFilter === 'all' && (
              <div className="mt-2 text-xs text-blue-100">
                Currently showing all forms
              </div>
            )}
          </div>

          <div 
            className={`rounded-lg shadow-md p-6 cursor-pointer transition-all duration-200 transform hover:scale-105 ${
              activeFilter === 'active' 
                ? 'bg-green-500 text-white ring-4 ring-green-300' 
                : 'bg-white hover:shadow-lg'
            }`}
            onClick={() => handleFilterClick('active')}
          >
            <div className="flex items-center">
              <Shield className={`w-8 h-8 ${activeFilter === 'active' ? 'text-white' : 'text-green-500'}`} />
              <div className="ml-4">
                <p className={`text-sm font-medium ${activeFilter === 'active' ? 'text-green-100' : 'text-gray-600'}`}>
                  Active Forms
                </p>
                <p className={`text-2xl font-bold ${activeFilter === 'active' ? 'text-white' : 'text-gray-900'}`}>
                  {activeForms}
                </p>
              </div>
            </div>
            {activeFilter === 'active' && (
              <div className="mt-2 text-xs text-green-100">
                Showing active forms only
              </div>
            )}
          </div>

          <div 
            className={`rounded-lg shadow-md p-6 cursor-pointer transition-all duration-200 transform hover:scale-105 ${
              activeFilter === 'coming-soon' 
                ? 'bg-orange-500 text-white ring-4 ring-orange-300' 
                : 'bg-white hover:shadow-lg'
            }`}
            onClick={() => handleFilterClick('coming-soon')}
          >
            <div className="flex items-center">
              <Calendar className={`w-8 h-8 ${activeFilter === 'coming-soon' ? 'text-white' : 'text-orange-500'}`} />
              <div className="ml-4">
                <p className={`text-sm font-medium ${activeFilter === 'coming-soon' ? 'text-orange-100' : 'text-gray-600'}`}>
                  Coming Soon
                </p>
                <p className={`text-2xl font-bold ${activeFilter === 'coming-soon' ? 'text-white' : 'text-gray-900'}`}>
                  {comingSoonForms}
                </p>
              </div>
            </div>
            {activeFilter === 'coming-soon' && (
              <div className="mt-2 text-xs text-orange-100">
                Showing coming soon forms
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <Building className="w-8 h-8 text-purple-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Categories</p>
                <p className="text-2xl font-bold text-gray-900">{categories.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Active Filter Indicator */}
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-gray-600">Currently showing:</span>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              activeFilter === 'all' 
                ? 'bg-blue-100 text-blue-800' 
                : activeFilter === 'active'
                ? 'bg-green-100 text-green-800'
                : 'bg-orange-100 text-orange-800'
            }`}>
              {activeFilter === 'all' ? 'All Forms' : activeFilter === 'active' ? 'Active Forms' : 'Coming Soon Forms'}
            </span>
            <span className="text-gray-500">({filteredForms.length} forms)</span>
          </div>
          
          {activeFilter !== 'all' && (
            <button
              onClick={() => handleFilterClick('all')}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              Clear Filter
            </button>
          )}
        </div>

        {/* Forms by Category */}
        {categories.length > 0 ? (
          categories.map(category => (
            <div key={category} className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">{category}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredForms
                  .filter(form => form.category === category)
                  .map(form => (
                    <div
                      key={form.id}
                      className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-200"
                    >
                      {/* Form Card Header */}
                      <div className={`${form.color} p-6 text-white`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            {form.icon}
                            <div className="ml-4">
                              <h3 className="text-xl font-bold">{form.title}</h3>
                              <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full mt-2 ${
                                form.status === 'Active' 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {form.status}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Form Card Body */}
                      <div className="p-6">
                        <p className="text-gray-600 mb-6 h-12 overflow-hidden">
                          {form.description}
                        </p>

                        {/* Action Buttons */}
                        <div className="flex space-x-3">
                          <button
                            onClick={() => handleFormNavigation(form.route, form.status)}
                            disabled={form.status !== 'Active'}
                            className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all duration-200 ${
                              form.status === 'Active'
                                ? `${form.color} ${form.hoverColor} text-white hover:transform hover:scale-105`
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }`}
                          >
                            {form.status === 'Active' ? 'Open Form' : 'Coming Soon'}
                          </button>
                          
                          {form.status === 'Active' && (
                            <button
                              onClick={() => handleFormEdit(form.route, form.status)}
                              className="py-2 px-4 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200"
                            >
                              Edit
                            </button>
                          )}
                        </div>

                        {/* Quick Info */}
                        <div className="mt-4 pt-4 border-t border-gray-100">
                          <div className="flex items-center text-sm text-gray-500">
                            <FileText className="w-4 h-4 mr-2" />
                            <span>Route: {form.route}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-600 mb-2">No forms found</h3>
            <p className="text-gray-500">
              {activeFilter === 'active' 
                ? 'No active forms available' 
                : 'No forms coming soon'}
            </p>
          </div>
        )}

        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-lg p-8 mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Quick Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => navigate('/forms/create')}
              className="flex items-center justify-center py-4 px-6 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-all duration-200"
            >
              <FileText className="w-5 h-5 mr-2" />
              Create New Form
            </button>
            <button
              onClick={() => navigate('/forms/templates')}
              className="flex items-center justify-center py-4 px-6 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-all duration-200"
            >
              <Building className="w-5 h-5 mr-2" />
              Form Templates
            </button>
            <button
              onClick={() => navigate('/forms/submissions')}
              className="flex items-center justify-center py-4 px-6 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-medium transition-all duration-200"
            >
              <Shield className="w-5 h-5 mr-2" />
              View Submissions
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-12 py-8 border-t border-gray-200">
          <div className="flex items-center justify-center mt-4 space-x-6">
            {/* <div className="flex items-center text-gray-500">
              <Mail className="w-4 h-4 mr-2" />
              <span>support@kajaria.com</span>
            </div> */}
            {/* <div className="flex items-center text-gray-500">
              <Phone className="w-4 h-4 mr-2" />
              <span>+1 (555) 123-4567</span>
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllEforms;